import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loadAcceptJs } from "@/lib/acceptJs";

var API = "/api";

function digitsOnly(s) { return (s || "").replace(/\D/g, ""); }

function formatCardNumberDisplay(digits) {
  // Group into 4s for readability (visual only — we strip before sending)
  return digits.replace(/(.{4})/g, "$1 ").trim();
}

export default function AuthorizeNetCardForm({
  paymentIntentId,        // order id returned by /api/anet/create-order
  amount,                 // for the button label
  customerName,
  onSuccess,
  onError
}) {
  var [config, setConfig] = useState(null);
  var [configError, setConfigError] = useState(null);
  var [acceptReady, setAcceptReady] = useState(false);
  var [submitting, setSubmitting] = useState(false);

  var [cardholderName, setCardholderName] = useState(customerName || "");
  var [cardNumber, setCardNumber] = useState("");
  var [expMonth, setExpMonth] = useState("");
  var [expYear, setExpYear] = useState("");
  var [cardCode, setCardCode] = useState("");
  var [fieldError, setFieldError] = useState("");

  // Fetch Accept.js config (public client key + api login id) from backend
  useEffect(function () {
    var cancelled = false;
    axios.get(API + "/anet/config")
      .then(function (res) {
        if (cancelled) return;
        if (!res.data || !res.data.enabled) {
          setConfigError("Credit card payment is not currently configured.");
          return;
        }
        setConfig(res.data);
      })
      .catch(function () {
        if (!cancelled) setConfigError("Could not load card form configuration.");
      });
    return function () { cancelled = true; };
  }, []);

  // Load Accept.js as soon as we have a config (sandbox vs production has different script hosts)
  useEffect(function () {
    if (!config) return;
    loadAcceptJs(config.mode)
      .then(function () { setAcceptReady(true); })
      .catch(function (err) {
        console.error("Accept.js load error:", err);
        setConfigError("Could not load secure card form. Please refresh and try again.");
      });
  }, [config]);

  function validate() {
    var digits = digitsOnly(cardNumber);
    if (digits.length < 13 || digits.length > 19) return "Please enter a valid card number.";
    var m = parseInt(expMonth, 10);
    if (isNaN(m) || m < 1 || m > 12) return "Please enter a valid expiration month (01-12).";
    var y = parseInt(expYear, 10);
    if (isNaN(y)) return "Please enter a valid expiration year.";
    if (expYear.length === 2) y = 2000 + y;
    var now = new Date();
    if (y < now.getFullYear() || (y === now.getFullYear() && m < (now.getMonth() + 1))) {
      return "This card appears to be expired.";
    }
    var cvv = digitsOnly(cardCode);
    if (cvv.length < 3 || cvv.length > 4) return "Please enter a valid security code (CVV).";
    if (!cardholderName.trim()) return "Please enter the cardholder name.";
    return null;
  }

  function handleSubmit(e) {
    e.preventDefault();
    setFieldError("");
    if (submitting) return;

    var validationErr = validate();
    if (validationErr) {
      setFieldError(validationErr);
      return;
    }
    if (!window.Accept) {
      setFieldError("Card form not ready. Please wait a moment and try again.");
      return;
    }
    if (!paymentIntentId) {
      setFieldError("Order not ready. Please go back and try again.");
      return;
    }

    setSubmitting(true);

    var year2 = expYear.length === 4 ? expYear.slice(-2) : expYear.padStart(2, "0");
    var month2 = expMonth.length === 1 ? "0" + expMonth : expMonth;

    var data = {
      authData: {
        clientKey: config.public_client_key,
        apiLoginID: config.api_login_id
      },
      cardData: {
        cardNumber: digitsOnly(cardNumber),
        month: month2,
        year: year2,
        cardCode: digitsOnly(cardCode),
        fullName: cardholderName.trim()
      }
    };

    window.Accept.dispatchData(data, function (response) {
      if (!response || response.messages.resultCode === "Error") {
        var msg = "Could not verify card details.";
        if (response && response.messages && response.messages.message && response.messages.message[0]) {
          var m = response.messages.message[0];
          // Map common Accept.js codes to friendlier text
          if (m.code === "E_WC_05") msg = "Invalid card number. Please check and try again.";
          else if (m.code === "E_WC_06") msg = "Invalid expiration date.";
          else if (m.code === "E_WC_07") msg = "Card has expired.";
          else if (m.code === "E_WC_08") msg = "Invalid CVV.";
          else if (m.code === "E_WC_15") msg = "Invalid card type.";
          else if (m.code === "E_WC_17") msg = "Card data could not be verified.";
          else msg = m.text || msg;
        }
        setFieldError(msg);
        setSubmitting(false);
        return;
      }

      var opaqueData = response.opaqueData;
      axios.post(API + "/anet/charge", {
        payment_intent_id: paymentIntentId,
        opaque_data: {
          dataDescriptor: opaqueData.dataDescriptor,
          dataValue: opaqueData.dataValue
        },
        cardholder_name: cardholderName.trim()
      })
        .then(function (res) {
          if (res.data && res.data.status === "success") {
            onSuccess && onSuccess(res.data);
          } else {
            setFieldError("Payment did not complete. Please try again.");
            onError && onError(res.data);
          }
        })
        .catch(function (err) {
          var detail = err.response?.data?.detail || "Payment failed. Please try a different card or contact support.";
          setFieldError(detail);
          onError && onError(err);
          toast.error(detail);
        })
        .finally(function () { setSubmitting(false); });
    });
  }

  if (configError) {
    return (
      <div className="bg-[#121212] border border-red-500/30 rounded-sm p-6 text-[#A1A1AA] text-sm" data-testid="anet-config-error">
        {configError}
      </div>
    );
  }

  if (!config || !acceptReady) {
    return (
      <div className="bg-[#121212] border border-white/5 rounded-sm p-6 flex items-center text-[#A1A1AA] text-sm" data-testid="anet-loading">
        <Loader2 className="w-4 h-4 mr-2 animate-spin text-[#C0A062]" />
        Loading secure card form...
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-[#121212] border border-white/5 rounded-sm p-6 space-y-4" data-testid="anet-card-form">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[#C0A062]">
          <CreditCard className="w-5 h-5" />
          <span className="font-mono text-xs uppercase tracking-wider">Credit or Debit Card</span>
        </div>
        <div className="flex items-center gap-1 text-[#71717a] text-[10px] font-mono uppercase">
          <Lock className="w-3 h-3" /> Secure
        </div>
      </div>

      <div>
        <Label htmlFor="anet-cardholder" className="text-[#A1A1AA] text-xs uppercase">Name on Card</Label>
        <Input
          id="anet-cardholder"
          value={cardholderName}
          onChange={function (e) { setCardholderName(e.target.value); }}
          placeholder="John A. Doe"
          autoComplete="cc-name"
          className="input-tactical"
          data-testid="anet-cardholder"
          required
        />
      </div>

      <div>
        <Label htmlFor="anet-cardnumber" className="text-[#A1A1AA] text-xs uppercase">Card Number</Label>
        <Input
          id="anet-cardnumber"
          value={formatCardNumberDisplay(cardNumber)}
          onChange={function (e) { setCardNumber(digitsOnly(e.target.value).slice(0, 19)); }}
          placeholder="1234 5678 9012 3456"
          inputMode="numeric"
          autoComplete="cc-number"
          className="input-tactical font-mono"
          data-testid="anet-cardnumber"
          required
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label htmlFor="anet-exp-month" className="text-[#A1A1AA] text-xs uppercase">Month</Label>
          <Input
            id="anet-exp-month"
            value={expMonth}
            onChange={function (e) { setExpMonth(digitsOnly(e.target.value).slice(0, 2)); }}
            placeholder="MM"
            inputMode="numeric"
            autoComplete="cc-exp-month"
            className="input-tactical font-mono"
            data-testid="anet-exp-month"
            required
          />
        </div>
        <div>
          <Label htmlFor="anet-exp-year" className="text-[#A1A1AA] text-xs uppercase">Year</Label>
          <Input
            id="anet-exp-year"
            value={expYear}
            onChange={function (e) { setExpYear(digitsOnly(e.target.value).slice(0, 4)); }}
            placeholder="YYYY"
            inputMode="numeric"
            autoComplete="cc-exp-year"
            className="input-tactical font-mono"
            data-testid="anet-exp-year"
            required
          />
        </div>
        <div>
          <Label htmlFor="anet-cvv" className="text-[#A1A1AA] text-xs uppercase">CVV</Label>
          <Input
            id="anet-cvv"
            value={cardCode}
            onChange={function (e) { setCardCode(digitsOnly(e.target.value).slice(0, 4)); }}
            placeholder="123"
            inputMode="numeric"
            autoComplete="cc-csc"
            className="input-tactical font-mono"
            data-testid="anet-cvv"
            required
          />
        </div>
      </div>

      {fieldError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3 text-red-300 text-sm" data-testid="anet-error">
          {fieldError}
        </div>
      )}

      <Button
        type="submit"
        className="btn-primary w-full"
        disabled={submitting}
        data-testid="anet-pay-btn"
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
        ) : (
          <><Lock className="w-4 h-4 mr-2" /> Pay ${(amount || 0).toFixed(2)}</>
        )}
      </Button>

      <p className="text-center text-[#71717a] text-[10px] font-mono uppercase tracking-wider">
        Card data is tokenized by Authorize.Net — never stored on our servers.
      </p>
    </form>
  );
}
